import {
	checkHasApprovalHierarchy,
	compareStrings,
	disabledByAnotherAction,
	getCurrency,
	getObjectValues,
	highestTierSplitPartiallyApproved,
	isBuyerAdminUser,
	isHierarchyNTEBased,
	isInvoiceTotalGreaterThanNTE,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import * as Sentry from '@sentry/react';
import { Button, message, Popover } from 'antd';
import moment from 'moment';
import * as React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import ApprovalHierarchyDisplayInvoiceModal from './ApprovalHierarchyDisplayInvoiceModal';
import DeclineInvoiceForm from '../decline_invoice_form/DeclineInvoiceForm';
import { connect } from 'react-redux';
import { UndoOutlined } from '@ant-design/icons';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	isAllowedToPerform,
	isBuyerOrInternalTech,
	isSupplierAllowedToAccess,
	isThirdPartySupplier,
	PERMISSION_NAMES,
} from '../../utils/AuthUtils';
import ForwardApproval from '../forward_approval/ForwardApproval';
import {
	forwardApprovalToUserForBuyer,
	forwardApprovalToUserForSupplier,
} from '../../thunks/company_approval_thunks';
import _ from 'lodash';
import { UserName } from '../name_component/user/UserNameComponent';
import { joinListElements } from '../../utils/DataFormatterUtils';
import { infinityButNumber } from '../../constants/general';
import MoveBackToPendingButton from '../invoices_detail_page/MoveBackToPendingButton';
import { showBackToPendingForPaidandProcessing } from '../../constants/InvoiceStatuses';
import { isClientInvoiceView } from '../invoice_utilities/InvoiceUtilities';
import ViewPastRevisionsButton from '../view_past_revisions_button/ViewPastRevisionsButton';

function ApprovalHierarchyDisplayInvoice({
	workOrder,
	invoice,
	currentUser,
	approveInvoice,
	declineInvoice,
	syncInvoice,
	markInvoicePaid,
	markInvoiceProcessing,
	markInvoicePending,
	appendWorkOrderNotes,
	refresh,
	companyConfig,
	style = {},
	allowedToApprove = true,
	userType,

	history,
	forwardApprovalToUser,
}) {
	const isClientView = isClientInvoiceView(invoice, currentUser);
	const INVOICE_STATUSES = {
		APPROVED: 'approved',
		PAID: 'paid',
		PROCESSING: 'processing',
		PENDING: 'pending',
		DISPUTED: 'disputed',
	};
	const defaultActionsAllowed =
		userType === ROLE_TYPES.SUPPLIER
			? isSupplierAllowedToAccess(
					PERMISSION_NAMES.CAN_APPROVE_INVOICES,
					companyConfig,
					currentUser.roles
			  )
			: isAllowedToPerform('canApproveInvoices', companyConfig, currentUser.roles);

	const allowMarkProcessingOrPaid =
		userType === ROLE_TYPES.SUPPLIER
			? isSupplierAllowedToAccess(
					PERMISSION_NAMES.CAN_MARK_INVOICES_PROCESSING_OR_PAID,
					companyConfig,
					currentUser.roles
			  )
			: isAllowedToPerform(
					PERMISSION_NAMES.CAN_MARK_INVOICES_PROCESSING_OR_PAID,
					companyConfig,
					currentUser.roles
			  );
	const [showModal, setShowModal] = useState(false);
	const [actionButtonLoading, setActionButtonLoading] = useState(null);
	const [declining, setDeclining] = useState(false);
	const [showDeclineModal, setShowDeclineModal] = useState(false);
	const status = nullSafeGet('status', invoice);
	const reason = useRef();
	const setReason = (str) => (reason.current = str);
	const showStatus = (status) => {
		switch (status) {
			case 'pending':
				return 'Pending Approvers';
			case 'disputed':
				return 'Approvers/Disputers';
			case 'approved':
				return 'Approvers';
			case 'voided':
				return 'Approver List';
			default:
				return 'Approver List';
		}
	};
	const hasApprovalHierarchy = checkHasApprovalHierarchy(invoice);
	const hasApprovers = (tiers) =>
		tiers.flatMap((tier) => tier.approvers).some((approvers) => approvers.length > 0);
	const userTier = (tiers) =>
		hasApprovalHierarchy
			? tiers.filter((tier) =>
					tier.approvers.some((approver) => compareStrings(approver.email, currentUser.email))
			  ).length > 0
				? tiers.find((tier) =>
						tier.approvers.some((approver) => compareStrings(approver.email, currentUser.email))
				  )
				: undefined
			: tiers[0];

	const currency = getCurrency({ workOrder: nullSafeGet('workOrder', invoice) });
	const buyerCompanyCurrency = getCurrency({
		buyerCompany: nullSafeGet('workOrder.buyerCompany', invoice),
	});
	const exchangeRate =
		currency.id === buyerCompanyCurrency.id
			? 1
			: nullSafeGetOrElse('workOrder.currencyExchangeRateLocationToSupplier', invoice, 1) *
			  nullSafeGetOrElse('workOrder.currencyExchangeRateBuyerToLocation', invoice, 1);
	let invoiceTotal = nullSafeGet('invoiceTotalAfterTax', invoice) / exchangeRate;
	let lessThanInvoice = true;
	let highestValidAmount = 0;
	let highestApprovedAmount = 0;
	const highestApprovalTierAmount = (tiers) => {
		let highestTiers = [...tiers]
			.filter((tier) => {
				let approvedByUsers = nullSafeGet('approvedByUsers', tier);
				let declinedByUsers = nullSafeGet('declinedByUsers', tier);
				if (approvedByUsers && declinedByUsers) {
					return approvedByUsers.concat(declinedByUsers).length > 0;
				} else {
					return false;
				}
			})
			.map((tier) => {
				return nullSafeGetOrElse('amountUpto', tier, infinityButNumber);
			});
		if (highestTiers.length > 0) {
			return highestTiers[highestTiers.length - 1];
		}
		return 0;
	};

	let lastAmount = 0;
	const isNTEBased = isHierarchyNTEBased(
		nullSafeGetOrElse('approvalHierarchy.tierDetails', invoice, [])
	);
	const tiers = nullSafeGet('approvalHierarchy.tierDetails', invoice)
		? nullSafeGet('approvalHierarchy.tierDetails', invoice)
				//Sort tiers primarily by tier.amountUpto, secondarily by tier.label
				.sort((a, b) =>
					a.amountUpto && b.amountUpto
						? parseFloat(a.amountUpto) > parseFloat(b.amountUpto)
							? 1
							: parseInt(a.label) > parseInt(b.label)
							? 1
							: -1
						: a.amountUpto === undefined
						? 1
						: -1
				)
				//Setup tier objects
				.map((tier) => {
					if (nullSafeGetOrElse('approvedByUsers', tier, []).length > 0) {
						highestApprovedAmount = parseInt(nullSafeGet('amountUpto', tier)) || Infinity;
					}
					return {
						...tier,
						approvedByUsers: nullSafeGetOrElse('approvedByUsers', tier, []).filter(
							(user, i, users) => i === users.findIndex((u) => compareStrings(u.email, user.email))
						),
						declinedByUsers: nullSafeGetOrElse('declinedByUsers', tier, []).filter(
							(user, i, users) => i === users.findIndex((u) => compareStrings(u.email, user.email))
						),
					};
				})
				.map((tier, i, tiers) => {
					let message;
					let isVisible = () => {
						if (!isNTEBased) {
							//We want to see all tiers when it is not NTE Based
							return true;
						}

						//We also want to display tiers which fit one of the following criteria:
						let amount = parseInt(nullSafeGet('amountUpto', tier));

						//1.This tier has either approvers or decliners and is >= the highest amount approved
						if (
							highestApprovedAmount > amount &&
							tier.approvedByUsers.length === 0 &&
							tier.declinedByUsers.length === 0
						) {
							//Not shown because a higher tier already approved
							message = 'A higher tier has already approved';
							return false;
						}
						if (
							highestApprovedAmount === amount &&
							tier.approvers.length === 0 &&
							tier.approvedByUsers.length === 0
						) {
							//Not shown because an equal tier has already approved and this tier has no approvers
							message =
								'An equal tier has approved and this tier has no approvers for this location';
							return false;
						}
						if (!lessThanInvoice) {
							let lastAmount = parseInt(nullSafeGetOrElse('amountUpto', tiers[i - 1], 0));
							//3. This tier has already been approved or declined
							if (tier.approvedByUsers.length > 0 || tier.declinedByUsers.length > 0) {
								return true;
							} else {
								//4. This tier is the same amount as the previous tier
								if (highestValidAmount === amount && highestValidAmount === lastAmount) {
									return true;
								} else {
									message = `Approval from this tier isn't required for invoices less than ${currency.format(
										(lastAmount ? lastAmount : amount) * exchangeRate
									)}`;
									return false;
								}
							}
						}

						if (amount && invoiceTotal) {
							if (parseFloat(amount) > parseFloat(invoiceTotal)) {
								//2. This tier is one tier over the invoice and contains potential approvers
								highestValidAmount = amount;
								if (nullSafeGetOrElse('approvers.length', tier, 0) > 0) {
									lessThanInvoice = false;
								}
							}
						} else {
							//1. This tier is less than the invoice
							lessThanInvoice = false;
						}
						return true;
					};
					lastAmount =
						nullSafeGetOrElse('amountUpto', tiers[i - 1], 0) === nullSafeGet('amountUpto', tier)
							? lastAmount
							: nullSafeGetOrElse('amountUpto', tiers[i - 1], 0);
					return { ...tier, isVisible: isVisible(), message, lastAmount };
				})
		: [];
	const highestAmount = highestApprovalTierAmount(tiers);
	const showApproval = [INVOICE_STATUSES.PENDING, INVOICE_STATUSES.DISPUTED].some((status) =>
		compareStrings(status, invoice.status)
	);
	// this condition is because of concurrency on workorder status , where a user updates something on the stale state.
	if (
		highestApprovedAmount > invoiceTotal &&
		!highestTierSplitPartiallyApproved(tiers, highestApprovedAmount) &&
		showApproval
	) {
		// the highest approved amount is greater than the nte
		// and the work order is still pending. there is some bug. so we will reset the tiers so that nobody has approved.
		Sentry.captureException(`Error state for Invoice: Resetting Approval Hierarchy ${invoice.id}`);
		tiers.forEach((tier) => {
			tier.approvedByUsers = [];
			tier.declinedByUsers = [];
		});
	}

	let myTier = userTier(tiers);
	const myTierAmount = nullSafeGet('amountUpto', myTier);
	const highestTier = (tiers || []).at(-1);
	const isHighestTier = compareStrings(
		nullSafeGet('label', highestTier),
		nullSafeGet('label', myTier)
	);
	const currentInvoice = {
		...invoice,
		approvalHierarchy: { ...invoice.approvalHierarchy, tierDetails: tiers },
		highestAmount: highestAmount,
	};

	const canMarkInvoicePaid = useMemo(() => {
		const isPendingInvoice = compareStrings(invoice.status, INVOICE_STATUSES.PENDING);
		/*For a user to mark an invoice paid
		1. The invoice should have been uploaded by the buyer
		2. There shouldn't be any approval hierarchy
		3. If there is an approval hierarchy, current user Approving should be able to approve the whole invoice
		4. Current invoice status should be in pending status
		*/
		const allowedByApprovalHierarchy = hasApprovalHierarchy
			? isNTEBased
				? myTier && (myTierAmount === undefined || parseFloat(myTierAmount) >= invoiceTotal)
				: isHighestTier
			: allowMarkProcessingOrPaid;

		return isPendingInvoice && invoice.isBuyerUploaded && allowedByApprovalHierarchy;
	}, [
		invoice.status,
		invoice.isBuyerUploaded,
		INVOICE_STATUSES.PENDING,
		hasApprovalHierarchy,
		isNTEBased,
		myTier,
		myTierAmount,
		invoiceTotal,
		isHighestTier,
		allowMarkProcessingOrPaid,
	]);

	const showBackToApproved = [INVOICE_STATUSES.PROCESSING].some((status) =>
		compareStrings(status, invoice.status)
	);

	const allowApproval = useMemo(() => {
		if (status !== 'approved') {
			if (hasApprovalHierarchy && myTier !== undefined) {
				//Has approval Hierarchy and a tier
				if (myTierAmount === undefined || parseFloat(myTierAmount) >= highestAmount) {
					if (
						!nullSafeGetOrElse('approvedByUsers', myTier, []).some((user) =>
							compareStrings(currentUser.email, user.email)
						)
					) {
						return true;
					} else {
						setReason('Your tier has already approved.');
						return false;
					}
				}
			} else if (!hasApprovalHierarchy) {
				//No approval Hierarchy , default to access control
				return defaultActionsAllowed;
			}
			setReason('You are not in the approval hierarchy.');
		} else {
			setReason('This invoice has already been approved.');
		}
		return false;
	}, [
		status,
		currentUser.email,
		hasApprovalHierarchy,
		myTier,
		myTierAmount,
		highestAmount,
		defaultActionsAllowed,
	]);
	const showDecline = [INVOICE_STATUSES.PENDING].some((status) =>
		compareStrings(status, invoice.status)
	);
	const allowDecline = useMemo(() => {
		if (status !== 'approved' && status !== 'disputed') {
			if (hasApprovalHierarchy && myTier) {
				//Has an approval hierarchy and a tier
				if (myTierAmount === undefined || parseFloat(myTierAmount) >= highestAmount) {
					if (
						!nullSafeGetOrElse('approvedByUsers', myTier, []).some((user) =>
							compareStrings(currentUser.email, user.email)
						)
					) {
						return true;
					} else {
						setReason('Your tier has already approved.');
						return false;
					}
				}
			} else if (!hasApprovalHierarchy && myTier !== undefined) {
				//No approval Hierarchy
				if (
					nullSafeGetOrElse('approvedByUsers', myTier, []).some((user) =>
						compareStrings(currentUser.email, user.email)
					)
				) {
					setReason('You have already approved.');
					return false;
				} else if (nullSafeGetOrElse('approvedByUsers', myTier, []).length > 1) {
					setReason('Someone has already approved.');
					return false;
				} else {
					setReason(`You are not allowed to approve/dispute invoice.`);
					return defaultActionsAllowed;
				}
			} else if (!hasApprovalHierarchy && myTier === undefined) {
				setReason(`You are not allowed to approve/dispute invoice.`);
				return defaultActionsAllowed;
			}
		}
		if (status === 'disputed') {
			setReason('This invoice has already been disputed.');
		}
		return false;
	}, [status, hasApprovalHierarchy, myTier, myTierAmount, highestAmount, defaultActionsAllowed]);

	const handleApproveInvoice = useCallback(
		(invoice) => {
			let myTier = userTier(tiers)
				? userTier(tiers)
				: {
						approvers: [],
						approvedByUsers: [],
						declinedByUsers: [],
						approvedByType: 'any',
						label: '1',
				  };

			//Add this user to approvedByUsers list
			myTier.approvedByUsers.push({
				email: currentUser.email.toLowerCase(),
				approverType: 'user',
				contact: currentUser,
			});
			let newinvoice = {
				...invoice,
				approvalHierarchy: {
					...invoice.approvalHierarchy,
					tierDetails: invoice.approvalHierarchy.tierDetails.map((tier) => {
						return tier.id === myTier.id ? { ...myTier } : tier;
					}),
				},
			};
			setActionButtonLoading(INVOICE_STATUSES.APPROVED);
			message.open({
				key: 'invoiceDetailMessageUpdating',
				type: 'loading',
				content: 'Approving...',
				duration: 60,
			});
			approveInvoice(newinvoice, isClientView).then((resultInvoice) => {
				setActionButtonLoading(null);
				message.destroy('invoiceDetailMessageUpdating');
				message.success('Approved!');
				refresh();
			});
		},
		[invoice, approveInvoice]
	);
	const showPaid = [INVOICE_STATUSES.PROCESSING, INVOICE_STATUSES.APPROVED].some((s) =>
		compareStrings(s, status)
	);
	const handlePaidInvoice = useCallback(() => {
		setActionButtonLoading(INVOICE_STATUSES.PAID);
		message.open({
			key: 'invoiceDetailMessageUpdating',
			type: 'loading',
			content: 'Marking invoice as paid...',
			duration: 60,
		});
		markInvoicePaid(invoice, isClientView).then((resultInvoice) => {
			setActionButtonLoading(null);
			message.destroy('invoiceDetailMessageUpdating');
			message.success(`Invoice paid.`);
			refresh();
		});
	}, [invoice, markInvoicePaid]);
	const showProcessing = [INVOICE_STATUSES.APPROVED].some((s) => compareStrings(s, status));
	const showBackToProcessing = [INVOICE_STATUSES.PAID].some((s) => compareStrings(s, status));
	const handleProcessingInvoice = useCallback(() => {
		setActionButtonLoading(INVOICE_STATUSES.PROCESSING);
		message.open({
			key: 'invoiceDetailMessageUpdating',
			type: 'loading',
			content: 'Marking invoice as processing...',
			duration: 60,
		});
		markInvoiceProcessing(invoice, isClientView).then(() => {
			setActionButtonLoading(null);
			message.destroy('invoiceDetailMessageUpdating');
			message.success(`Invoice is being processed.`);
			refresh();
		});
	}, [invoice, markInvoiceProcessing]);
	const showBackToPending = [INVOICE_STATUSES.APPROVED].some((s) => compareStrings(s, status));
	const handlePendingInvoice = useCallback(() => {
		setActionButtonLoading(INVOICE_STATUSES.PENDING);
		message.open({
			key: 'invoiceDetailMessageUpdating',
			type: 'loading',
			content: 'Marking invoice as pending...',
			duration: 60,
		});
		markInvoicePending(invoice, isClientView).then(() => {
			setActionButtonLoading(null);
			message.destroy('invoiceDetailMessageUpdating');
			message.success(`Invoice is pending.`);
			refresh();
		});
	}, [invoice, markInvoicePending]);

	const handleDisputeInvoice = (invoice) => {
		setShowDeclineModal(true);
	};
	const handleSubmitDisputeInvoice = (values) => {
		let myTier = userTier(tiers)
			? userTier(tiers)
			: {
					approvers: [],
					approvedByUsers: [],
					declinedByUsers: [],
					approvedByType: 'any',
					label: '1',
			  };

		//Add this user to declining user list
		myTier.declinedByUsers.push({
			email: currentUser.email.toLowerCase(),
			approverType: 'user',
			contact: currentUser,
		});

		//If this user is currently approving, remove them from approving list
		myTier.approvedByUsers = myTier.approvedByUsers.filter((user) => {
			return user.email.toLowerCase() !== currentUser.email.toLowerCase();
		});

		setActionButtonLoading(INVOICE_STATUSES.DISPUTED);
		setDeclining(true);
		const workOrderNoteDetails = {
			text: 'Invoice Disputed: '.concat(values.note),
			noteAddedBy: nullSafeGet('email', currentUser),
			noteAddedAt: moment.utc(),
		};
		declineInvoice(
			{ id: currentInvoice.id, contractorWorkOrderId: currentInvoice.contractorWorkOrderId },
			workOrderNoteDetails,
			isClientView
		)
			.then((resultInvoice) => {
				const note = {
					text: 'Invoice Disputed: '.concat(values.note),
					noteAddedBy: nullSafeGet('email', currentUser),
					noteAddedAt: moment.utc(),
				};
				const workOrderNoteDetails = {
					id: workOrder.id,
					locationId: workOrder.locationId,
					note,
				};

				appendWorkOrderNotes(workOrderNoteDetails)
					.then(() => {
						message.success(`Invoice disputed.`);
						setShowDeclineModal(false);
						refresh();
					})
					.catch((err) => message.error(err))
					.finally(() => {
						setDeclining(false);
						setActionButtonLoading(null);
					});
			})
			.catch((err) => {
				message.error(err);
				setDeclining(false);
			});
	};

	const isInApprovalHierarchy = hasApprovalHierarchy && myTier !== undefined;
	const allowMoveBackToPending =
		isInApprovalHierarchy ||
		(!hasApprovalHierarchy && (defaultActionsAllowed || isInApprovalHierarchy));

	const approvalForwardingEnabled =
		userType === 'supplier'
			? _.get(
					currentUser,
					'facility.privateBuyerCompanySettings.config.approvalHierarchyConfig.addUserToInvoiceApprovalHierarchyOnTheGo',
					true
			  )
			: _.get(
					companyConfig,
					'config.approvalHierarchyConfig.addUserToInvoiceApprovalHierarchyOnTheGo',
					true
			  );
	//Has approval hierarchy and is in it, or there is no approval hierarchy and therefore can approve
	const isInHierarchyOrNoHierarchy =
		(hasApprovalHierarchy && myTier !== undefined) || !hasApprovalHierarchy;
	const showForwardForApproval =
		status === 'pending' &&
		isInHierarchyOrNoHierarchy &&
		approvalForwardingEnabled &&
		!isThirdPartySupplier(currentUser);

	const approvalForwardDetails = nullSafeGetOrElse('approvalForwardedDetails', invoice, []);
	const approvalList = {};
	approvalForwardDetails.map(
		(user) =>
			(approvalList[nullSafeGet('forwardedTo', user)] = nullSafeGet('forwardedToContact', user))
	);
	const forwardedList = getObjectValues(approvalList);
	const showBackToPendingForPaidandMore = isBuyerOrInternalTech(currentUser)
		? showBackToPendingForPaidandProcessing(invoice)
		: false;
	const totalIsGreaterThanNTE = isInvoiceTotalGreaterThanNTE(invoice, workOrder);
	if (totalIsGreaterThanNTE) {
		setReason(
			'This invoice was published prior to the work order NTE amount changing. Please revise the NTE or suggest supplier to update the invoice.'
		);
	}

	return (
		<div className={'flex-col'} style={{ marginBottom: 20 }}>
			<div className={'flex flex-row items-baseline'}>
				<DeclineInvoiceForm
					visible={showDeclineModal}
					onCancel={() => {
						setShowDeclineModal(false);
						setActionButtonLoading(null);
					}}
					declining={declining}
					onSubmit={handleSubmitDisputeInvoice}
				/>
				<ApprovalHierarchyDisplayInvoiceModal
					isNTEBased={isNTEBased}
					tiers={tiers}
					hasApprovalHierarchy={hasApprovalHierarchy}
					currentInvoice={currentInvoice}
					setVisible={setShowModal}
					visible={showModal}
					exchangeRate={exchangeRate}
					currency={currency}
					syncInvoice={syncInvoice}
					refresh={refresh}
				/>
				<h5 style={{ display: 'flex' }}>
					{currentInvoice && currentInvoice.status && allowMarkProcessingOrPaid
						? compareStrings(currentInvoice.status, 'pending')
							? 'This invoice is on hold until it has been fully approved'
							: compareStrings(currentInvoice.status, 'approved')
							? 'Has this invoice been paid?'
							: compareStrings(currentInvoice.status, 'disputed')
							? 'This invoice has been disputed'
							: compareStrings(currentInvoice.status, 'processing')
							? 'Has this invoice been paid?'
							: compareStrings(currentInvoice.status, 'paid')
							? 'Do you want to move this back to processing?'
							: 'This invoice has been voided.'
						: null}
				</h5>
				<ViewPastRevisionsButton record={invoice} recordType={'invoice'} />
			</div>
			{forwardedList.length > 0 ? (
				<div className={'mb-2 flex-row'}>
					<span>
						Approval has been forwarded to {` `}
						{forwardedList.map((contact, i) => (
							<span>
								<UserName contact={contact} mode={'inline'} placement={'top'} />
								{joinListElements(forwardedList.length, i)}
							</span>
						))}
					</span>
				</div>
			) : null}
			{allowedToApprove ? (
				<div
					style={{
						display: 'inline-flex',
						position: 'relative',
					}}
				>
					<div>
						{showApproval && currentInvoice && currentInvoice.status !== 'approved' ? (
							allowApproval && !totalIsGreaterThanNTE ? (
								<Button
									type="primary"
									key="approve"
									style={{ marginRight: 16 }}
									loading={actionButtonLoading === INVOICE_STATUSES.APPROVED}
									disabled={disabledByAnotherAction(actionButtonLoading, INVOICE_STATUSES.APPROVED)}
									onClick={() => handleApproveInvoice(currentInvoice)}
								>
									Approve Invoice
								</Button>
							) : (
								<Popover
									disabled={allowApproval}
									title={`Why can't I approve?`}
									content={nullSafeGet('current', reason)}
								>
									<Button type="primary" key="approve" style={{ marginRight: 16 }} disabled={true}>
										Approve Invoice
									</Button>
								</Popover>
							)
						) : null}
						{canMarkInvoicePaid ? (
							<Button
								type={'ghost'}
								key="paid"
								style={{ marginRight: 16 }}
								loading={actionButtonLoading === INVOICE_STATUSES.PAID}
								disabled={disabledByAnotherAction(actionButtonLoading, INVOICE_STATUSES.PAID)}
								onClick={handlePaidInvoice}
							>
								Mark Invoice As Paid
							</Button>
						) : null}
						{showDecline && currentInvoice && currentInvoice.status !== 'approved' ? (
							allowDecline ? (
								<Button
									key="reject"
									style={{ marginRight: 16 }}
									onClick={() => {
										setActionButtonLoading(INVOICE_STATUSES.DISPUTED);
										handleDisputeInvoice(currentInvoice);
									}}
									loading={actionButtonLoading === INVOICE_STATUSES.DISPUTED}
									disabled={disabledByAnotherAction(actionButtonLoading, INVOICE_STATUSES.DISPUTED)}
								>
									Dispute Invoice
								</Button>
							) : (
								<Popover title={`Why can't I dispute?`} content={nullSafeGet('current', reason)}>
									<Button key="reject" style={{ marginRight: 16 }} disabled={true}>
										Dispute Invoice
									</Button>
								</Popover>
							)
						) : null}
						{showPaid && allowMarkProcessingOrPaid ? (
							<Button
								type={'primary'}
								key="paid"
								style={{ marginRight: 16 }}
								loading={actionButtonLoading === INVOICE_STATUSES.PAID}
								disabled={disabledByAnotherAction(actionButtonLoading, INVOICE_STATUSES.PAID)}
								onClick={handlePaidInvoice}
							>
								Yes, it was paid
							</Button>
						) : null}
						{showProcessing && allowMarkProcessingOrPaid ? (
							<Button
								key="Processing"
								style={{ marginRight: 16 }}
								loading={actionButtonLoading === INVOICE_STATUSES.PROCESSING}
								disabled={disabledByAnotherAction(actionButtonLoading, INVOICE_STATUSES.PROCESSING)}
								onClick={handleProcessingInvoice}
							>
								It's being processed
							</Button>
						) : null}
						{showBackToPending && allowMoveBackToPending ? (
							<Button
								key="approve"
								style={{ marginRight: 16 }}
								loading={actionButtonLoading === INVOICE_STATUSES.PENDING}
								disabled={disabledByAnotherAction(actionButtonLoading, INVOICE_STATUSES.PENDING)}
								onClick={handlePendingInvoice}
								icon={<UndoOutlined translate="" />}
							>
								Move Back to Pending
							</Button>
						) : null}
						{showBackToProcessing && allowMarkProcessingOrPaid ? (
							<Button
								key="markProcessing"
								style={{ marginRight: 16 }}
								loading={actionButtonLoading === INVOICE_STATUSES.PROCESSING}
								disabled={disabledByAnotherAction(actionButtonLoading, INVOICE_STATUSES.PROCESSING)}
								onClick={handleProcessingInvoice}
								icon={<UndoOutlined translate="" />}
							>
								Move Back to Processing
							</Button>
						) : null}
						{showBackToApproved && allowMarkProcessingOrPaid ? (
							<Button
								key="approved"
								style={{ marginRight: 16 }}
								loading={actionButtonLoading === INVOICE_STATUSES.APPROVED}
								disabled={disabledByAnotherAction(actionButtonLoading, INVOICE_STATUSES.APPROVED)}
								onClick={() => handleApproveInvoice(currentInvoice)}
								icon={<UndoOutlined translate="" />}
							>
								Move back to approved
							</Button>
						) : null}
						{showBackToPendingForPaidandMore ? <MoveBackToPendingButton invoice={invoice} /> : null}
						{showForwardForApproval ? (
							<ForwardApproval
								disabled={!isInHierarchyOrNoHierarchy || actionButtonLoading !== null}
								reason={reason}
								workOrder={workOrder}
								entity={invoice}
								refresh={refresh}
								forwardApprovalToUser={forwardApprovalToUser}
								actionButtonLoading={actionButtonLoading}
							/>
						) : null}
						{hasApprovalHierarchy || hasApprovers(tiers) ? (
							<div
								className={'inline-flex flex-row items-center'}
								style={{ cursor: 'pointer', fontSize: '16px' }}
								onClick={() => setShowModal(true)}
							>
								<i className="icons8-font icons8-multiple-users" />
								<span style={{ marginLeft: '8px', textDecoration: 'underline' }}>
									{showStatus(status)}
								</span>
							</div>
						) : null}
					</div>
				</div>
			) : showBackToPendingForPaidandMore ? (
				<MoveBackToPendingButton invoice={invoice} />
			) : null}
		</div>
	);
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
});
const mapDispatchToProps = (dispatch, ownProps) => ({
	forwardApprovalToUser: (entity, email) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? forwardApprovalToUserForSupplier('invoice', entity, email)
				: forwardApprovalToUserForBuyer('invoice', entity, email)
		),
});

const ComponentWithoutUserType = connect(
	mapStateToProps,
	mapDispatchToProps
)(ApprovalHierarchyDisplayInvoice);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
		companyConfig: (state as any).company_config.detail,
	}),
	() => ({})
)(ComponentWithoutUserType);
